/**
 * MORE COMMUNITIES THAN THE BLOCK DREW — THE TABLE GROWS ITS OWN SECTIONS. 2026-08-22.
 *
 * Jack, four times now, last time flat out: "why can't you add a 3rd
 * collection/section like I've been asking..." — three communities picked, two
 * sections rendered. Every previous answer respected ECH_ListingTable's markup as a
 * limit: it draws `collection1Name` over rows 1–5 and `collection2Name` over rows
 * 6–10, so two was all it could label. His ruling stands above that markup:
 *
 *   "that is just a mockup — should be able to add more unless there are guardrails
 *    stating otherwise… not your role to be concerned."
 *
 * So the fill RE-CUTS the block's own rows instead of obeying their layout. The
 * heading row and the ten home rows are already self-describing — Layne delimits
 * them with `<!-- 1ST COLLECTION -->` and `<!-- HOME n -->` — so this clones her
 * heading row, renumbers it, and deals the same ten home rows out across as many
 * sections as there are communities: 3 → 4/3/3, 5 → 2 each. Nothing is invented;
 * every row is her row, in her order, with her tokens.
 *
 * WHAT THIS DELIBERATELY DOES NOT DO. It never adds an eleventh row — ten is a real
 * fact about the block (ten declared `home<N>*` field sets), and a row with no
 * declared fields would render Layne's tokens as literal text in a live send. The
 * cap on COMMUNITIES is therefore the row count, not the heading count.
 */

/** `<!-- 3RD COLLECTION -->` — the marker `echRows.dropEmptyCollectionHeaders` reads */
export function collOrdinal(n: number): string {
  const s = ["TH", "ST", "ND", "RD"][n % 100 > 10 && n % 100 < 14 ? 0 : Math.min(n % 10, 4) % 4] || "TH";
  return String(n) + s;
}

const HEAD_PAIR = /<!--\s*\d+(?:ST|ND|RD|TH)\s+COLLECTION\s*-->\s*<tr[\s\S]*?<\/tr>/gi;
const HOME_PAIR = /<!--\s*HOME\s+\d+\s*-->\s*<tr[\s\S]*?<\/tr>/gi;

/**
 * Re-cut a listing table so it carries exactly `wanted` collection sections.
 * Returns the html unchanged when there is nothing to re-cut (no heading marker, no
 * home rows, or the shape already matches).
 */
export function echExpandListingHtml(html: string, wanted: number, sizes?: number[]): string {
  const src = String(html || "");
  const heads = src.match(HEAD_PAIR) || [];
  const homes = src.match(HOME_PAIR) || [];
  if (!heads.length || homes.length < 2) return src;

  /* one section per community, but never more sections than rows to fill them */
  const G = Math.max(1, Math.min(Math.floor(wanted) || 1, homes.length));
  /* ── SIZES, WHEN THE CALLER KNOWS THEM (2026-08-22) ───────────────────────────
     An EVEN deal is right when the rows are still being chosen from a feed, and
     wrong when the requester has already picked: ten picks across five communities
     as 3/2/2/2/1 cut into 2/2/2/2/2 puts a heading over another community's home,
     which is the defect Jack hit ("selected a bunch of homes from different
     communities and for some reason created this"). A caller that knows how many
     rows each section owns passes them; anything else still gets the even deal. */
  const want = (sizes || []).map((n) => Math.max(0, Math.floor(n) || 0)).filter((n) => n > 0);
  const exact = want.length === G && want.reduce((a, b) => a + b, 0) <= homes.length ? want : null;
  if (G === heads.length && !exact) return src;

  /* the region to replace runs from the first heading marker to the last home row */
  const first = src.search(/<!--\s*\d+(?:ST|ND|RD|TH)\s+COLLECTION\s*-->/i);
  const lastHome = String(homes[homes.length - 1] || "");
  const endAt = src.lastIndexOf(lastHome) + lastHome.length;
  if (first < 0 || endAt <= first) return src;

  /* Layne's heading row, with its number swapped per section */
  const tmpl = String(heads[0] || "");
  const head = (n: number) =>
    tmpl
      .replace(/<!--\s*\d+(?:ST|ND|RD|TH)\s+COLLECTION\s*-->/i, "<!-- " + collOrdinal(n) + " COLLECTION -->")
      .replace(/\{\{collection\d+Name\}\}/g, "{{collection" + n + "Name}}");

  const base = Math.floor(homes.length / G);
  const rem = homes.length - base * G;
  const out: string[] = [];
  for (let g = 0, at = 0; g < G; g++) {
    const take = exact ? exact[g] : base + (g < rem ? 1 : 0);
    out.push(head(g + 1));
    for (let i = 0; i < take; i++) out.push(String(homes[at + i] || ""));
    at += take;
  }
  /* the block indents its rows; joining on a newline keeps `ownRow()`'s
     marker→row adjacency (it steps over whitespace text nodes) */
  return src.slice(0, first) + out.join("\n") + src.slice(endAt);
}
